import { useState } from 'react';
import { PERMISSION, can, useAuth } from '@wa/shared';
import { OverviewPanel } from '@/shop/OverviewPanel';
import { ProductsPanel } from '@/shop/ProductsPanel';
import { GroupsPanel } from '@/shop/GroupsPanel';
import { SettingsPanel } from '@/shop/SettingsPanel';
import { PaymentsPanel } from '@/shop/PaymentsPanel';
import { ImportPanel } from '@/shop/ImportPanel';

/**
 * The WhatsApp Shop, merchant side (design §03). Six tabs rather than six
 * destinations, the way Billing and Team are laid out.
 */
type Tab = 'overview' | 'products' | 'groups' | 'settings' | 'payments' | 'import';

const TABS: { key: Tab; label: string; blurb: string; needs?: string }[] = [
  { key: 'overview', label: 'Overview', blurb: 'Whether the shop is live, its address, and the link to share.' },
  { key: 'products', label: 'Products', blurb: 'What is for sale, with variants, pictures and prices per customer type.' },
  { key: 'groups', label: 'Customer types', blurb: 'Retail, dealer, distributor — who sees which prices and pays how.', needs: PERMISSION.shopManage },
  { key: 'settings', label: 'Page & delivery', blurb: 'The look of the page, contact details, delivery charges and pickup.', needs: PERMISSION.shopManage },
  { key: 'payments', label: 'Payments', blurb: 'Cash on delivery, on account, and your own payment gateway.', needs: PERMISSION.shopManage },
  { key: 'import', label: 'Import', blurb: 'Products and prices from a spreadsheet, checked before anything is written.', needs: PERMISSION.shopManage },
];

export function ShopPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const visible = TABS.filter((t) => !t.needs || can(user, t.needs));
  const current = visible.find((t) => t.key === tab) ?? visible[0] ?? TABS[0]!;

  return (
    <div className="pane-scroll h-full overflow-y-auto bg-(--color-paper)">
      <div className="@container mx-auto flex max-w-4xl flex-col gap-5 p-3 sm:p-5">
        <header className="flex flex-col gap-3">
          <h1 className="text-title font-semibold tracking-tight">Shop</h1>
          <div role="tablist" aria-label="Shop" className="chip-scroll flex items-center gap-1">
            {visible.map((item) => (
              <button key={item.key} type="button" role="tab" aria-selected={current.key === item.key} onClick={() => setTab(item.key)}
                      className={['tap shrink-0 rounded-(--radius-chip) px-2.5 py-1 text-micro font-medium transition',
                        current.key === item.key ? 'bg-(--color-brand) text-(--color-brand-ink)' : 'text-(--color-muted) hover:bg-(--color-sunken) hover:text-(--color-ink)'].join(' ')}>
                {item.label}
              </button>
            ))}
          </div>
          <p className="max-w-prose text-small text-(--color-muted)">{current.blurb}</p>
        </header>

        {current.key === 'overview' && <OverviewPanel />}
        {current.key === 'products' && <ProductsPanel />}
        {current.key === 'groups' && <GroupsPanel />}
        {current.key === 'settings' && <SettingsPanel />}
        {current.key === 'payments' && <PaymentsPanel />}
        {current.key === 'import' && <ImportPanel />}
      </div>
    </div>
  );
}
